import React from 'react';
import { AdvancedImage } from '@cloudinary/react';
import { resolveLink } from '@lib/resolveLinkProps';
import { getNavCardVariant } from '@lib/getNavCardVariant';
import useSelector from '@redux/typedHooks';
import { MainEntryType } from '../../../../types/siteStructureType';
import { useDocumentUrlWithNode } from '../../hooks/Documents/useDocumentUrl';
import { NavCardLink } from '../styled-link/StyledLink';
import { ShortCutCol, StyledCardNavigation } from './Styles';
import { useCloudinary } from '../../hooks/Cloudinary/useCloudinary';

export const MainEntryCard = ({ entry, col }: { entry: MainEntryType; col: string }) => {
  const application = useSelector((state) => state.application);
  const { variant, color } = getNavCardVariant(entry.colorVariant);

  // Image handling
  const { getProcessedImage } = useCloudinary();
  const itemImage =
    entry.illustration?.publicId &&
    getProcessedImage(entry.illustration.publicId, {
      width: 300,
      height: 300,
      resizeMethod: 'pad',
      format: 'auto',
    });

  // Link handling
  let isInternal = false;
  let internalItemUrl: string | undefined;
  let isBlank = false;

  // Handle the plain internal link case
  internalItemUrl = useDocumentUrlWithNode(entry.internalLink);
  if (entry.internalLink?.slug) {
    isInternal = true;

    // Check whether the internal link should open a new tab
    if (entry.internalLink?.blank) {
      isBlank = entry.internalLink.blank;
    }
  }

  // If not plain internal -> check whether the external link points to an internal address
  if (!isInternal && entry.externalLink?.href) {
    const resolved = resolveLink({ url: entry.externalLink.href, baseUrl: application.baseUrl });
    isInternal = resolved.isInternal;
    if (resolved.isInternal) {
      internalItemUrl = resolved.linkProps.to || '';
    }

    // Check whether the external link should open a new tab regardless of whether it is of external or internal type
    if (entry.externalLink.blank) {
      isBlank = entry.externalLink.blank;
    }
  }

  if (isInternal && internalItemUrl) {
    const cardContent = (
      <StyledCardNavigation
        title={entry.title || entry.internalLink?.name || ''}
        description={entry.description}
        size={parseInt(col, 10) <= 3 ? 'medium' : 'large'}
        illustration={itemImage ? <AdvancedImage cldImg={itemImage} alt={entry.illustration?.altText || ''} /> : null}
        height="100%"
        variant={variant}
        color={color}
        isInternalLink
      />
    );

    return (
      <ShortCutCol s="12" m="6" l={6} xl={col} key={internalItemUrl}>
        <NavCardLink
          to={internalItemUrl}
          target={isBlank ? '_blank' : undefined}
          rel={isBlank ? 'noopener noreferrer' : undefined}
        >
          {cardContent}
        </NavCardLink>
      </ShortCutCol>
    );
  }

  if (entry.externalLink?.href) {
    return (
      <ShortCutCol s="12" m="6" l="6" xl={col} key={entry.externalLink.href}>
        <StyledCardNavigation
          href={entry.externalLink?.href}
          target={isBlank ? '_blank' : '_self'}
          title={entry.title}
          description={entry.description}
          size={parseInt(col, 10) <= 3 ? 'medium' : 'large'}
          illustration={itemImage ? <AdvancedImage cldImg={itemImage} alt={entry.illustration?.altText || ''} /> : null}
          height="100%"
          variant={variant}
          color={color}
        />
      </ShortCutCol>
    );
  }

  return (
    <ShortCutCol s="12" m="6" l={6} xl={col} key={entry.title}>
      <StyledCardNavigation
        title={entry.title}
        description={entry.description}
        size={parseInt(col, 10) <= 3 ? 'medium' : 'large'}
        illustration={itemImage ? <AdvancedImage cldImg={itemImage} alt={entry.illustration?.altText || ''} /> : null}
        height="100%"
        variant={variant}
        color={color}
      />
    </ShortCutCol>
  );
};
